import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import LeadManagement from './LeadManagement';
import ChatSystem from './ChatSystem';
import FollowUpDashboard from './FollowUpDashboard';
import NotificationBell from './NotificationBell';
import {
  HomeIcon,
  ChartBarIcon,
  PhoneArrowUpRightIcon,
  Cog6ToothIcon,
  ChatBubbleLeftRightIcon,
  PresentationChartLineIcon,
} from '@heroicons/react/24/outline';

const DashboardLead = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('leads');
  const [showChat, setShowChat] = useState(false);
  const [unreadChatCount, setUnreadChatCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [followUps, setFollowUps] = useState([]);
  const [leadManagementTab, setLeadManagementTab] = useState('dashboard'); // For LeadManagement internal tabs

  const handleChatToggle = () => {
    setShowChat(prev => !prev);
  };

  const handleMarkAsRead = (notificationId) => {
    setNotifications(prev => prev.filter(n => n.id !== notificationId));
  };

  const handleMarkAllAsRead = () => {
    setNotifications([]);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* Header with Notification Bell */}
      {/* <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <h1 className="text-2xl font-bold text-gray-900">CRM Dashboard</h1>
            </div>
            <div className="flex items-center space-x-4">
              <NotificationBell 
                notifications={notifications}
                followUps={followUps}
                onMarkAsRead={handleMarkAsRead}
                onMarkAllAsRead={handleMarkAllAsRead}
              />
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
                  <span className="text-white text-sm font-medium">
                    {user?.name?.charAt(0) || 'U'}
                  </span>
                </div>
                <span className="text-sm font-medium text-gray-700">{user?.name || 'User'}</span>
              </div>
            </div>
          </div>
        </div>
      </header> */}

      {/* Main Content */}
      <main className="pt-0 pb-4 px-0 sm:py-4 sm:px-4">
        {/* Desktop Navigation - Hidden on mobile, original layout and color scheme */}
        <div className="hidden md:block bg-white/80 backdrop-blur-md shadow-xl rounded-2xl mb-4 border border-gray-200/50">
          <div className="border-b border-gray-200/50">
            <div className="flex justify-between items-center px-4 py-2 flex-wrap gap-2">
              {/* Left: Lead Management Tab */}
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setActiveTab('leads')}
                  className={`py-2 px-3 font-semibold text-sm transition-all duration-200 rounded ${
                    activeTab === 'leads'
                      ? 'text-blue-600 bg-blue-50'
                      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <PresentationChartLineIcon className="h-4 w-4" />
                  Lead Management
                </button>
              </div>

              {/* Right: Navigation Buttons for LeadManagement */}
              {activeTab === 'leads' && (
                <div className="flex items-center flex-wrap gap-1.5">
                  <button
                    onClick={() => setLeadManagementTab('dashboard')}
                    className={`px-3 py-1.5 rounded-lg font-semibold text-sm transition-all duration-200 ${leadManagementTab === 'dashboard'
                      ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                  >
                    <HomeIcon className="h-4 w-4 inline-block mr-1 -mt-0.5" />
                    Home
                  </button>

                  <button
                    onClick={() => setLeadManagementTab('analytics')}
                    className={`px-3 py-1.5 rounded-lg font-semibold text-sm transition-all duration-200 ${leadManagementTab === 'analytics'
                      ? 'bg-gradient-to-r from-green-600 to-emerald-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                  >
                    <ChartBarIcon className="h-4 w-4 inline-block mr-1 -mt-0.5" />
                    Analytics
                  </button>

                  <button
                    onClick={() => setLeadManagementTab('followups')}
                    className={`px-3 py-1.5 rounded-lg font-semibold text-sm transition-all duration-200 ${leadManagementTab === 'followups'
                      ? 'bg-gradient-to-r from-pink-600 to-rose-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                  >
                    <PhoneArrowUpRightIcon className="h-4 w-4 inline-block mr-1 -mt-0.5" />
                    Follow-ups
                  </button>

                  <button
                    onClick={() => setLeadManagementTab('settings')}
                    className={`px-3 py-1.5 rounded-lg font-semibold text-sm transition-all duration-200 ${leadManagementTab === 'settings'
                      ? 'bg-gradient-to-r from-purple-600 to-violet-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                  >
                    <Cog6ToothIcon className="h-4 w-4 inline-block mr-1 -mt-0.5" />
                    Settings
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Mobile Navigation - Visible only on mobile, glassmorphism blue style */}
        <div className="md:hidden mb-4 rounded-none sm:rounded-2xl p-4 bg-gradient-to-br from-[#0c1a4e] via-[#1a3a8f] to-[#1e56c8] shadow-lg border-b sm:border border-blue-400/30 relative overflow-hidden">
          {/* Glow orbs decorative */}
          <div style={{ position: "absolute", top: "-30px", right: "-20px", width: "120px", height: "120px", borderRadius: "50%", background: "radial-gradient(circle, rgba(96,165,250,0.22) 0%, transparent 70%)", pointerEvents: "none" }} />
          <div style={{ position: "absolute", bottom: "-20px", left: "-10px", width: "100px", height: "100px", borderRadius: "50%", background: "radial-gradient(circle, rgba(59,130,246,0.18) 0%, transparent 70%)", pointerEvents: "none" }} />

          <div className="flex flex-col gap-3.5 relative z-10">
            {/* Header / Title */}
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center space-x-2.5">
                <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0 bg-gradient-to-br from-blue-500 via-purple-500 to-pink-500 shadow-lg">
                  <PresentationChartLineIcon className="w-4 h-4 text-white" />
                </div>
                <div>
                  <h2 className="text-sm font-bold text-white tracking-wide whitespace-nowrap">
                    Lead Management
                  </h2>
                  <p className="text-[10px] font-medium text-blue-200">Dashboard & Analytics</p>
                </div>
              </div>
            </div>

            {/* Buttons Grid */}
            {activeTab === 'leads' && (
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => setLeadManagementTab('dashboard')}
                  className={`flex items-center justify-center space-x-1.5 py-2.5 rounded-xl text-xs font-bold active:scale-95 transition-all duration-200 shadow-sm border ${
                    leadManagementTab === 'dashboard'
                      ? 'bg-white/30 text-white border-white/50 backdrop-blur-md'
                      : 'bg-white/10 hover:bg-white/20 text-white border-white/20 backdrop-blur-md'
                  }`}
                >
                  <HomeIcon className="w-3.5 h-3.5 shrink-0" />
                  <span>Home</span>
                </button>

                <button
                  onClick={() => setLeadManagementTab('analytics')}
                  className={`flex items-center justify-center space-x-1.5 py-2.5 rounded-xl text-xs font-bold active:scale-95 transition-all duration-200 shadow-sm border ${
                    leadManagementTab === 'analytics'
                      ? 'bg-white/30 text-white border-white/50 backdrop-blur-md'
                      : 'bg-white/10 hover:bg-white/20 text-white border-white/20 backdrop-blur-md'
                  }`}
                >
                  <ChartBarIcon className="w-3.5 h-3.5 shrink-0" />
                  <span>Analytics</span>
                </button>

                <button
                  onClick={() => setLeadManagementTab('followups')}
                  className={`flex items-center justify-center space-x-1.5 py-2.5 rounded-xl text-xs font-bold active:scale-95 transition-all duration-200 shadow-sm border ${
                    leadManagementTab === 'followups'
                      ? 'bg-white/30 text-white border-white/50 backdrop-blur-md'
                      : 'bg-white/10 hover:bg-white/20 text-white border-white/20 backdrop-blur-md'
                  }`}
                >
                  <PhoneArrowUpRightIcon className="w-3.5 h-3.5 shrink-0" />
                  <span>Follow-ups</span>
                </button>

                <button
                  onClick={() => setLeadManagementTab('settings')}
                  className={`flex items-center justify-center space-x-1.5 py-2.5 rounded-xl text-xs font-bold active:scale-95 transition-all duration-200 shadow-sm border ${
                    leadManagementTab === 'settings'
                      ? 'bg-white/30 text-white border-white/50 backdrop-blur-md'
                      : 'bg-white/10 hover:bg-white/20 text-white border-white/20 backdrop-blur-md'
                  }`}
                >
                  <Cog6ToothIcon className="w-3.5 h-3.5 shrink-0" />
                  <span>Settings</span>
                </button>


              </div>
            )}
          </div>
        </div>

        {/* Tab Content */}
        <div className="space-y-6">
          {activeTab === 'leads' && (
            <LeadManagement 
              activeTab={leadManagementTab}
              setActiveTab={setLeadManagementTab}
            />
          )}
          {activeTab === 'followups' && (
            <FollowUpDashboard 
              onNotificationsUpdate={setNotifications}
              onFollowUpsUpdate={setFollowUps}
            />
          )}
        </div>
      </main>

      {/* Chat System */}
      <ChatSystem
        currentUser={user}
        isOpen={showChat}
        onToggle={handleChatToggle}
        hideFloatingButton={true}
        onUnreadCountChange={setUnreadChatCount}
      />
    </div>
  );
};

export default DashboardLead;
